import React from 'react';
import { PureComponent } from '../../../../dispatcher/modules';
import PuzzleInput from './PuzzleInput';
import {
  MaskedTextBoxChangeEvent
} from '@progress/kendo-react-inputs';
import {
  OPTIONS,
  PUZZLE_INPUT_WITH_EXISTING_VALIDATION_PROPS,
  REQUEST_CONFIG_TYPE
} from './Types';
import { Position } from '../../../../dispatcher/Types';
import uuid from 'uuid-random';
import * as _ from 'lodash';

/**
 * PuzzleInputWithExistingValidation component.
 *
 * This component expects the following props to be passed in:
 *
 * - requestConfig: an object that holds the configuration for remote validation requests.
 *   This object should contain:
 * - url: string that corresponds to the {method: head} endpoint for checking if the resource exists
 * - isSearchParams: true if we want to use params and false if we want to use urlParams for requests
 *
 *   Note: To enable validation, the 'name' prop must be passed to the component as a string,
 *   matching available validation type in the provided 'config' object. For example, to use
 *   the field as an email input, pass 'name={"email"}' as a prop. "email" should exist in config also for validation to work.
 *
 * In addition to these props, this component also requires the parent component to pass down its props
 * by spreading them using the syntax: {...this.props}. This is necessary for the component to work properly.
 *
 * Example usage:
 * ```
 * <PuzzleInputWithExistingValidation
 *    {...this.props}
 *    requestConfig={{
 *        url: '/testUsers',
 *        isSearchParams: true
 *    }}
 *    name={'example'}
 *    getFieldValidation={this.getFieldValidation}
 *    // Additional props go here
 * />
 * ```
 *
 * See the {@link PuzzleInputWithExistingValidationExample} component for a complete example of how to use this component...
 *
 * @returns {JSX.Element} - Rendered component.
 */

class PuzzleInputWithExistingValidation extends PureComponent<PUZZLE_INPUT_WITH_EXISTING_VALIDATION_PROPS> {
  moduleId = uuid();

  componentDidUpdate(prevProps: Readonly<PUZZLE_INPUT_WITH_EXISTING_VALIDATION_PROPS>) {
    if (!_.isEqual(prevProps.requestConfig, this.props.requestConfig)) {
      const { name, value } = this.props;
      this.onInputChange(null, {
        name,
        value
      });
    }
  }

  /**
   * Checks if the value is not empty or spaces.
   * @param {string} value - The value to check.
   * @returns {boolean} - True if the value is not empty, false otherwise.
   */
  public isNotEmptyOrSpace = (value: string): boolean => !!(_.isString(value) && value.trim() !== '')

  /**
   * Dispatches an action to check if resourceExists
   // * @param {object} requestConfig - the validation request configuration
   * @param {MaskedTextBoxChangeEvent} e
   * @param {OPTIONS} options
   */
  private onInputChange = (e: MaskedTextBoxChangeEvent, options?: OPTIONS):void => {
    const { handleChange } = this.props.form;
    const pureValue = e?.value || e?.target?.value || (options?.value ?? '');
    const name = e?.target?.name || e?.target?.props?.name || options?.name;
    const value = _.isFunction(this.props.modifyHeadValue) ? this.props.modifyHeadValue(pureValue) : pureValue;

    let { url, isSearchParams, params = {}, paramsName }: REQUEST_CONFIG_TYPE = this.props.requestConfig;
    params = isSearchParams ? {
      [paramsName ? paramsName : this.props.name]: value, 
      ...params 
    } : {};
    const valid = this.isNotEmptyOrSpace(pureValue as string) && (_.isFunction(this.props.shouldValidateHead) ? this.props.shouldValidateHead(pureValue) : true);
    const data = {
      fieldName: name,
      name,
      value,
      isSearchParams,
      params,
      url: isSearchParams ? url : `${url}/${value}`,
      valid,
      pureValue,
      moduleId: this.moduleId
    };
    data[this.props.name] = value;

    if (!options) {
      handleChange(e);
    }

    this.dispatch('CheckIfResourceExists', {
      pos: Position.end,
      buffer: 300
    }, data);
  };

  /**
   Listener for resourceExists
   @param {string} name - The name of the input field.
   @param {boolean} resourceExists - Indicates whether the resource exists or not.
   @param {string} value - The value of the input field.
   */
  public onValidation = (name: string, value: string, resourceExists: boolean = false): void => {
    const { getFieldValidation, onChange } = this.props;
    getFieldValidation(resourceExists, name, value);

    onChange({
      name,
      value
    });
  }

  render() {
    return (
      <PuzzleInput
        {...this.props}
        onChange={this.onInputChange}
      />
    );
  }
}

export default PuzzleInputWithExistingValidation;
